import { useTranslation } from '../i18n/I18nProvider'
import { Icon, type IconName } from './icons/Icon'

export interface ShareChannel {
  readonly id: string
  readonly label: string
  readonly icon: IconName
}

/**
 * Generic system-share mock, ported from `prototype/screens/shared.js`'s `shareSheet()`.
 * Presentational only — the invite copy, its recipient, and what "more" resolves to belong
 * to the screen that renders this (e.g. P7's invite flow). SMS and WhatsApp stay hard-coded
 * proper nouns; the third channel's label used to be a bare `'…'` — translated now, from
 * `common.json`'s `shareMore`, which is the one reason this otherwise-presentational component
 * reads the locale at all.
 */
export function ShareSheet({
  heading,
  message,
  channels,
  onSelect,
}: {
  readonly heading: string
  readonly message: string
  readonly channels?: readonly ShareChannel[]
  onSelect(channelId: string): void
}): JSX.Element {
  const { t } = useTranslation('common')
  const defaultChannels: readonly ShareChannel[] = [
    { id: 'sms', label: 'SMS', icon: 'phone' },
    { id: 'whatsapp', label: 'WhatsApp', icon: 'message-circle' },
    { id: 'more', label: t('shareMore'), icon: 'ellipsis' },
  ]
  const resolvedChannels = channels ?? defaultChannels

  return (
    <div className="share-sheet">
      <div className="sh-head">{heading}</div>
      <div className="sh-msg">{message}</div>
      {resolvedChannels.map((channel) => (
        <button
          key={channel.id}
          className="row"
          type="button"
          data-interactive="true"
          onClick={() => onSelect(channel.id)}
        >
          <span className="l">
            <Icon name={channel.icon} size={15} /> {channel.label}
          </span>
          <span className="r">
            <Icon name="chevron-right" size={13} />
          </span>
        </button>
      ))}
    </div>
  )
}
